import React from "react";
import { Link } from "react-router-dom";

/**
 * PILLS brand lockup — the new logo asset has the "PILL'S" wordmark BAKED IN
 * below the P symbol, so the component just renders the image (no separate text).
 *
 * Variants:
 *   - "lockup" (DEFAULT)  → full lockup image (P symbol + "PILL'S" word baked in)
 *   - "mark"              → just the P symbol (cropped variant — pills-mark.png)
 *
 * Optional decorations:
 *   - tagline:  small muted text rendered to the RIGHT of the lockup (e.g. on login)
 *   - sublabel: small uppercase pill (e.g. "ADMIN OS") to the right of the lockup
 *
 * Sizes control the lockup image height. Because the new asset includes the wordmark,
 * we render it taller than the old square glyph so the baked-in text stays legible.
 */
const LOCKUP_SIZES = {
    // height of the lockup image. The lockup is roughly 1:1 (symbol + word stacked).
    // v3F: bumped again — user reported the navbar logo wasn't visible enough.
    // We want the brand to land prominently across navbar, footer and auth pages.
    xs: { img: "h-12", tag: "text-[10px]", sub: "text-[9px]"  },
    sm: { img: "h-16", tag: "text-[11px]", sub: "text-[10px]" },
    md: { img: "h-24", tag: "text-xs",     sub: "text-[10px]" },
    lg: { img: "h-36", tag: "text-sm",     sub: "text-xs"     },
};

const MARK_SIZES = {
    xs: "h-8 w-8",
    sm: "h-10 w-10",
    md: "h-14 w-14",
    lg: "h-20 w-20",
};

const Brand = ({
    size = "md",
    variant = "lockup", // back-compat: "full" → treated as "lockup"
    tagline,
    sublabel,
    to = "/",
    className = "",
    testId = "brand-lockup",
}) => {
    const isMark = variant === "mark";
    const sizes = LOCKUP_SIZES[size] || LOCKUP_SIZES.md;
    const markCls = MARK_SIZES[size] || MARK_SIZES.md;

    const img = isMark ? (
        <img
            src="/pills-mark.png"
            alt="PILLS"
            className={`${markCls} shrink-0 object-contain`}
            draggable="false"
        />
    ) : (
        <img
            src="/pills-logo.png"
            alt="PILLS"
            className={`${sizes.img} w-auto shrink-0 object-contain`}
            draggable="false"
        />
    );

    const accents = (sublabel || tagline) ? (
        <span className="flex min-w-0 flex-col leading-tight">
            {sublabel ? (
                <span
                    className={`rounded-md bg-zinc-100 px-1.5 py-0.5 font-semibold uppercase tracking-[0.12em] text-zinc-500 dark:bg-white/10 dark:text-zinc-400 ${sizes.sub}`}
                >
                    {sublabel}
                </span>
            ) : null}
            {tagline ? (
                <span className={`mt-0.5 text-zinc-500 dark:text-zinc-400 ${sizes.tag}`}>
                    {tagline}
                </span>
            ) : null}
        </span>
    ) : null;

    const inner = (
        <span className={`inline-flex items-center gap-2.5 transition-opacity hover:opacity-85 ${className}`}>
            {img}
            {accents}
        </span>
    );

    return to ? (
        <Link to={to} aria-label="PILLS home" data-testid={testId} className="inline-flex items-center">
            {inner}
        </Link>
    ) : (
        <span aria-label="PILLS" data-testid={testId} className="inline-flex items-center">
            {inner}
        </span>
    );
};

export default Brand;
